import { Auth0Provider } from "@auth0/auth0-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { RecoilRoot } from "recoil";
import { ThemeProvider } from "@athena/ui";
import { AuthorizedApolloProvider } from "@/lib/apollo";
import { auth0Config } from "@/lib/auth0";
import { queryClient } from "@/lib/query-client";
import { RequireAuth } from "@/components/RequireAuth";
import { FlagIdentitySync } from "@/components/FlagIdentitySync";
import { AdminPilotGate } from "@/components/AdminPilotGate";
import {
    BROADCAST,
    CHAT,
    CLIENT_IMPORT,
    CLIENT_QUEUE,
    CONTACT_LOG,
    DASHBOARD,
    MY_PROFILE,
    PUSH_NOTIFICATIONS,
} from "@/lib/adminPilot";
import { RootLayout } from "@/layouts/RootLayout";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { GalleryPage } from "./pages/GalleryPage";
import { OrganizationsPage } from "./pages/Organizations/OrganizationsPage";
import { UsersPage } from "./pages/Users/UsersPage";
import { OrganizationPage } from "./pages/Organizations/OrganizationPage";
import { UserDetail } from "./pages/Users/UserDetail";
import { CoursesPage } from "./pages/Courses/CoursesPage";
import { InboxPage } from "./pages/InboxPage";
import { TagsPage } from "./pages/Tags/TagsPage";
import { PresentersPage } from "./pages/Presenters/PresentersPage";
import { PushNotificationsPage } from "./pages/PushNotificationsPage";
import CoursePreview from "./pages/Courses/CoursePreviewPage";
import { CrisisManagementPage } from "./pages/CrisisManagementPage";
import { LoginPage } from "./pages/LoginPage";
import { AssignmentsPage } from "./pages/Assignments/AssignmentsPage";
import { AssignmentPage } from "./pages/Assignments/AssignmentPage";
import { CourseLayout } from "./layouts/CourseLayout";
import { LibraryPage } from "./pages/LibraryPage";
import { BulkUploadPage } from "./pages/BulkUpload/BulkUploadPage";
import { UploadQueueProvider } from "./pages/BulkUpload/UploadQueueProvider";
import { UploadQueueIndicator } from "./pages/BulkUpload/UploadQueueIndicator";
import { VideoAnalyticsPage } from "./pages/VideoAnalyticsPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { DataPage } from "./pages/Data/DataPage";
import { StatsPage } from "./pages/StatsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { HomePage } from "./pages/HomePage";
import { AlumniLayout } from "./layouts/AlumniLayout";
import { AlumniDirectoryPage } from "./pages/Alumni/AlumniDirectoryPage";
import { ClientProfileOverview, AlumniProfilePage } from "./pages/Alumni/AlumniProfilePage";
import { AlumniRecordPage } from "./pages/Alumni/AlumniRecordPage";
import { OutreachQueuePage } from "./pages/Alumni/OutreachQueuePage";
import { ClientImportPage } from "./pages/Alumni/ClientImportPage";
import { ContactLogPage } from "./pages/Alumni/ContactLogPage";
import { DashboardPage } from "./pages/Alumni/DashboardPage";
import { BroadcastPage } from "./pages/Alumni/BroadcastPage";
import { AlumniPushNotificationsPage } from "./pages/Alumni/AlumniPushNotificationsPage";

export function App() {
    return (
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
            <RecoilRoot>
                <Auth0Provider {...auth0Config}>
                    <AuthorizedApolloProvider>
                        <QueryClientProvider client={queryClient}>
                            <FlagIdentitySync />
                            <BrowserRouter>
                                {/* Above the routes, so uploads outlive the page that started them. */}
                                <UploadQueueProvider>
                                    <Routes>
                                        <Route path="login" element={<LoginPage />} />
                                        <Route element={<RequireAuth />}>
                                            <Route element={<AdminPilotGate />}>
                                                <Route element={<RootLayout />}>
                                                    <Route index element={<HomePage />} />
                                                    <Route path="gallery" element={<GalleryPage />} />
                                                    <Route path="courses" element={<CoursesPage />} />
                                                    <Route
                                                        path="analytics/video/:contentId"
                                                        element={<VideoAnalyticsPage />}
                                                    />
                                                    <Route path="assignments" element={<AssignmentsPage />} />
                                                    <Route path="assignments/:id" element={<AssignmentPage />} />
                                                    <Route path="course/new" element={<CoursePreview />} />
                                                    <Route path="library" element={<LibraryPage />} />
                                                    <Route path="library/upload" element={<BulkUploadPage />} />
                                                    <Route path="stats" element={<StatsPage />} />
                                                    <Route path="inbox" element={<InboxPage />} />
                                                    <Route
                                                        path="crisis-management"
                                                        element={<CrisisManagementPage />}
                                                    />
                                                    <Route path="tags" element={<TagsPage />} />
                                                    <Route path="presenters" element={<PresentersPage />} />
                                                    <Route
                                                        path="push-notifications"
                                                        element={<PushNotificationsPage />}
                                                    />
                                                    <Route path="organizations" element={<OrganizationsPage />} />
                                                    <Route path="organizations/:id" element={<OrganizationPage />} />
                                                    <Route path="integrations" element={<IntegrationsPage />} />
                                                    <Route path="data" element={<DataPage />} />
                                                    <Route path="users" element={<UsersPage />} />
                                                    <Route path="users/:id" element={<UserDetail />} />
                                                    <Route path="profile" element={<ProfilePage />} />
                                                    <Route path="*" element={<NotFoundPage />} />
                                                </Route>
                                                <Route element={<CourseLayout />}>
                                                    <Route path="course-preview/:id" element={<CoursePreview />} />
                                                </Route>
                                                <Route path="alumni" element={<AlumniLayout />}>
                                                    <Route index element={<AlumniDirectoryPage />} />
                                                    <Route path={CLIENT_QUEUE} element={<OutreachQueuePage />} />
                                                    <Route path={CONTACT_LOG} element={<ContactLogPage />} />
                                                    <Route path={CLIENT_IMPORT} element={<ClientImportPage />} />
                                                    <Route path={DASHBOARD} element={<DashboardPage />} />
                                                    <Route path={BROADCAST} element={<BroadcastPage />} />
                                                    <Route
                                                        path={PUSH_NOTIFICATIONS}
                                                        element={<AlumniPushNotificationsPage />}
                                                    />
                                                    <Route path={CHAT} element={<InboxPage />} />
                                                    <Route path={MY_PROFILE} element={<ProfilePage />} />
                                                    <Route path=":clientId" element={<AlumniProfilePage />}>
                                                        <Route index element={<ClientProfileOverview />} />
                                                        <Route
                                                            path="record"
                                                            element={<Navigate to="notes" replace />}
                                                        />
                                                        <Route path="record/:tab" element={<AlumniRecordPage />} />
                                                    </Route>
                                                </Route>
                                            </Route>
                                        </Route>
                                    </Routes>
                                    <UploadQueueIndicator />
                                </UploadQueueProvider>
                            </BrowserRouter>
                        </QueryClientProvider>
                    </AuthorizedApolloProvider>
                </Auth0Provider>
            </RecoilRoot>
        </ThemeProvider>
    );
}
